/* eslint-disable react/no-array-index-key */

import React from 'react';
import styled from 'styled-components';
import { Link } from './link';
import {
  colors, ColoredSpan, BoldSpan,
} from '../components/home/v2/styledComponents';

const SubtitleSpan = styled.span`
  font-size: 20px;
  font-weight: bold;
`;

const Li = styled.li`
  margin-top: 5px;
  margin-bottom: 5px;
`;

export const SpecsSpan = styled.span`
  font-size: 12px;
  font-weight: 400;
  margin-left: 5px;
`;

// return 01 for 1, 12 for 12 and 34 for 134 (edge case)
// => do not use for number that can be greater than 99
export function toTwoDigits(number) {
  if (Number.isNaN(number) || number < 0) return '00';
  if (number !== Math.floor(number)) return String(number);
  return (`0${number}`).slice(-2) || '00';
}

// returns 01 for 1, 12 for 12 and 134 for 134
// returns 32.5 for 32.5
export function toTwoDigitsOrMore(number) {
  if (Number.isNaN(number) || number < 0) return '00';
  if (number >= 100) return number;
  if (number !== Math.floor(number)) return String(number);
  return (`0${number}`).slice(-2) || '00';
}

// returns 1,00 for 1, 12,00 for 12 and 134,00 for 134
// returns 32,50 for 32.5, 28,04 for 28.045615161
export function toTwoDecimalDigits(number) {
  if (Number.isNaN(number) || number < 0) return '00,00';
  const numberIt = number.toFixed(2).replace('.', ',');
  if (number >= 10) return numberIt;
  return (`0${numberIt}`) || '00,00';
}

// returns
//    Hello,
//    world
// for Hello, \nworld
export function slashNtoBr(text, nbsp = false) {
  const splitText = text.split('\n');
  if (nbsp) {
    return splitText.map((line, index) => (
      <>
        <span key={`${line}${index}`}>
          {line}
        </span>
        {splitText.length - 1 !== index && <br />}
      </>
    ));
  }
  return splitText.map((line, index) => (
    <span key={`${line}${index}`}>
      {line}
      {splitText.length - 1 !== index && <br />}
    </span>
  ));
}

// return « Hello, world » for Hello, world
export function toWrappedText(text) {
  return (
    <span>
      {'« '}
      {text}
      {' »'}
    </span>
  );
}

// returns
//    Hello,
//    world
// for Hello, \nworld
// return if wrap ===true «Hello, world»
export function toFormattedText(text, wrap = false, nbsp = false) {
  if (!wrap) return slashNtoBr(text, nbsp);
  return toWrappedText(slashNtoBr(text, nbsp));
}

function toFormattedList(text) {
  const splitText = text.split('<li>');
  return (
    <ul>
      {splitText.map((item) => !!item && <Li key={item}>{item}</Li>)}
    </ul>
  );
}

function toFormattedSubtitleText(text) {
  return <SubtitleSpan>{text}</SubtitleSpan>;
}

function LinkOrA(link, linkStyle, subtext) {
  const style = { color: colors.black, ...linkStyle };
  if (link && link.includes('http')) {
    return (
      <a target="_blank" rel="noopener noreferrer" href={link} style={style}>
        {toFormattedText(subtext)}
      </a>
    );
  } if (link && !link.includes('http')) {
    return (
      <Link to={link} style={style}>
        {toFormattedText(subtext)}
      </Link>
    );
  }
  return null;
}

/**
 * Transforme :
 *  - les segments <link>…<link>  → LinkOrA(...)
 *  - les marques Markdown [label](url) → <a href=… target="_blank" rel="noopener noreferrer">
 *  - les sous-titres <subtitle>…<subtitle> → toFormattedSubtitleText
 *  - le texte ordinaire → toFormattedText
 */
export function toFormattedLinkedText(
  text,
  links = [],
  linkStyle = {},
  hasToCopyLinks = true,
) {
  const copiedLinks = hasToCopyLinks ? links.slice() : links;

  const markdownParsed = text
    .split(/(\[.*?\]\(https?:\/\/[^\s)]+\))/g)
    .filter(Boolean)
    .map((segment, idx) => {
      const m = segment.match(/^\[(.*?)\]\((https?:\/\/[^\s)]+)\)$/);
      if (m) {
        const [, label, url] = m;
        return (
          <a
            key={`md-${idx}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
          >
            {label}
          </a>
        );
      }
      return <React.Fragment key={`md-${idx}`}>{segment}</React.Fragment>;
    });

  return markdownParsed.flatMap((node, idx) => {
    if (React.isValidElement(node) && node.type === 'a') {
      return node;
    }

    const raw = typeof node === 'string'
      ? node
      : node.props.children;

    return raw.split('<link>').map((chunk, i) => (
      <span key={`link-${idx}-${i}`}>
        {i % 2 === 1
          ? LinkOrA(copiedLinks.shift(), linkStyle, chunk)
          : chunk
            .split('<subtitle>')
            .flatMap((sub2, j) => (
              j % 2 === 1
                ? toFormattedSubtitleText(sub2)
                : toFormattedText(sub2)
            ))}
      </span>
    ));
  });
}

export function toFormattedTextWithList(text, links = undefined, linkStyle = {}) {
  const splitText = text.split('<ul>').filter((line) => line !== '');
  return splitText.map((line, index) => {
    if (!links) {
      return (
        <span key={`${line}${index}`}>
          {index % 2 !== 0 ? toFormattedList(line) : toFormattedText(line)}
        </span>
      );
    }
    return (
      <span key={`${line}${index}`}>
        {index % 2 !== 0 ? toFormattedList(line) : toFormattedLinkedText(line, links, linkStyle, false)}
      </span>
    );
  });
}

// returns
//    Hello world (white)
// for <white>Hello world<white>
// returns
//    Hello world (yellow)
// for <yellow>Hello world<yellow>
export function toFormattedColoredText(text, color = 'white') {
  const splitText = text.split(`<${color}>`).filter((line) => line !== '');
  if (text.indexOf(`<${color}>`) === 0) {
    return splitText.map((line, index) => (
      <ColoredSpan key={`${line}${index}`} color={index % 2 === 0 ? color : ''}>
        {toFormattedText(line, false, true)}
      </ColoredSpan>
    ));
  }
  return splitText.map((line, index) => (
    <ColoredSpan key={`${line}${index}`} color={index % 2 !== 0 ? color : ''}>
      {toFormattedText(line, false, true)}
    </ColoredSpan>
  ));
}

// returns by default
//    Hello world (font-weight === 500)
// for <bold>Hello world<bold> or **Hello world**
export function toFormattedBoldText(
  text,
  fontWeight = 500,
  links = undefined,
  linkStyle = {},
) {
  // Normalisation Markdown → balises <bold>
  const normalized = text.replace(/\*\*(.+?)\*\*/g, '<bold>$1<bold>');

  const splitText = normalized.split('<bold>').filter((line) => line !== '');

  const copyLinks = links && links.slice();

  const startsWithBold = normalized.indexOf('<bold>') === 0;

  return splitText.map((line, index) => {
    const isBold = startsWithBold
      ? index % 2 === 0
      : index % 2 !== 0;

    return (
      <BoldSpan key={`${line}-${index}`} fontWeight={isBold ? fontWeight : 400}>
        {toFormattedTextWithList(line, copyLinks, linkStyle)}
      </BoldSpan>
    );
  });
}

// return
// Commande for 1. Commande
// Commande for 121.Commande
// cSpell: disable
// mmande   for Co.mmande
// cSpell: enable
// Commande for Commande
export function toUnNumberedText(text) {
  const start = text.indexOf('.') + 1;
  if (start === 0) return text;
  return text.slice(start).trimStart();
}

export function toFormattedCreationSizeAnswer(answer) {
  if (['Petit', 'Moyen', 'Grand', 'Classique'].some((size) => answer.startsWith(size))) {
    const size = answer.split(' ')[0];
    const specs = answer.substring(size.length);
    return (
      <>
        {size}
        <SpecsSpan>{specs}</SpecsSpan>
      </>
    );
  }
  return <span>{answer}</span>;
}

export const formattedName = (name) => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

export const capitalizeWords = (name) => {
  const words = name.split(' ');
  const capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  return capitalizedWords.join(' ');
};

export const truncateText = (text, maxWords) => {
  const words = text.split(' ');
  if (words.length > maxWords) {
    return `${words.slice(0, maxWords).join(' ')}...`;
  }
  return text;
};

// Inserts line breaks (<br />) to ensure each line does not exceed `charLimit` characters, while preserving whole words.
export const insertLineBreakAfterChars = (text, charLimit) => {
  if (charLimit === 0) return text;

  const words = text.split(' ');
  const result = [];
  let currentLine = '';

  words.forEach((word, index) => {
    if ((currentLine + word).length > charLimit) {
      result.push(<React.Fragment key={`line-${index}`}>{currentLine.trim()}</React.Fragment>, <br key={`br-${index}`} />);
      currentLine = `${word} `;
    } else {
      currentLine += `${word} `;
    }
  });

  if (currentLine.trim()) {
    result.push(<React.Fragment key="last-line">{currentLine.trim()}</React.Fragment>);
  }

  return result;
};
